// : Add - 创建 axios 工具函数文件 
import { ErrorCode, HttpError } from './error-codes.js';
import { Toast } from 'vant';

// Token 管理工具
export const TokenManager = {
  /**
   * 获取 token
   * @returns {{accessToken: string, refreshToken: string} | null}
   */
  getToken() {
    const accessToken = localStorage.getItem('ACCESS_TOKEN') || sessionStorage.getItem('ACCESS_TOKEN');
    const refreshToken = localStorage.getItem('REFRESH_TOKEN') || sessionStorage.getItem('REFRESH_TOKEN');
    
    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }
    return null;
  },

  /**
   * 设置 token
   * @param {{access_token: string, refresh_token: string} | null} token
   * @param {boolean} rememberMe - 是否记住登录状态
   */
  setToken(token, rememberMe = true) {
    if (token) {
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('ACCESS_TOKEN', token.access_token);
      storage.setItem('REFRESH_TOKEN', token.refresh_token);
    } else {
      // 清除 token
      localStorage.removeItem('ACCESS_TOKEN');
      localStorage.removeItem('REFRESH_TOKEN');
      sessionStorage.removeItem('ACCESS_TOKEN');
      sessionStorage.removeItem('REFRESH_TOKEN');
    }
  },

  /**
   * 清除 token
   */
  clearToken() {
    this.setToken(null);
  }
};

// 刷新 token 队列管理
let isRefreshing = false;
const retryQueue = [];

/**
 * Token 刷新函数
 * @param {import('axios').AxiosInstance} axiosInstance - axios 实例
 * @param {import('axios').AxiosError} error - 原始错误
 * @returns {Promise}
 */
export async function refreshToken(axiosInstance, error) {
  const config = error.config;
  
  // 如果正在刷新，将请求加入队列
  if (isRefreshing) {
    return new Promise((resolve) => {
      retryQueue.push((token) => {
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          resolve(axiosInstance(config));
        } else {
          resolve(Promise.reject(new HttpError(
            ErrorCode.Http_RefreshError,
            '登录已过期，请重新登录',
            error
          )));
        }
      });
    });
  }

  isRefreshing = true;
  const token = TokenManager.getToken();

  if (!token || !token.refreshToken) {
    isRefreshing = false;
    Toast.fail('登录已过期，请重新登录');
    // 跳转到登录页
    setTimeout(() => {
      window.location.href = '/#/login';
    }, 1500);
    return Promise.reject(new HttpError(
      ErrorCode.Http_RefreshError,
      '登录已过期，请重新登录',
      error
    ));
  }

  try {
    // : Modify - 使用封装的 oauth2Token API }}
    // 动态导入避免循环依赖
    const { oauth2Token } = await import('@/api/auth.js');

    const newToken = await oauth2Token('refreshToken', token.refreshToken);

    if (newToken?.access_token && newToken?.refresh_token) {
      // 保存新 token
      TokenManager.setToken({
        accessToken: newToken.access_token,
        refreshToken: newToken.refresh_token
      });

      isRefreshing = false;

      // 重试队列中的请求
      retryQueue.forEach(callback => callback(newToken.access_token));
      retryQueue.length = 0;

      // 重试当前请求
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newToken.access_token}`;
      return axiosInstance(config);
    } else {
      throw new Error('刷新 token 失败');
    }
  } catch (err) {
    isRefreshing = false;
    retryQueue.forEach(callback => callback(null));
    retryQueue.length = 0;

    Toast.fail('登录已过期，请重新登录');
    setTimeout(() => {
      window.location.href = '/#/login';
    }, 1500);

    return Promise.reject(new HttpError(
      ErrorCode.Http_RefreshError,
      '登录已过期，请重新登录',
      error
    ));
  }
}

/**
 * 超时重试函数
 * @param {import('axios').AxiosInstance} axiosInstance - axios 实例
 * @param {import('axios').AxiosError} error - 原始错误
 * @returns {Promise}
 */
export function timeoutRetry(axiosInstance, error) {
  const config = error.config;
  
  // 设置重试次数（默认最多重试1次）
  config.__retryCount = config.__retryCount || 0;
  
  if (config.__retryCount >= 1) {
    return Promise.reject(new HttpError(
      ErrorCode.Http_ErrTimeout,
      `请求超时：${config.url}`,
      error
    ));
  }
  
  config.__retryCount += 1;
  
  // 延迟重试
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(axiosInstance(config));
    }, 1000);
  });
}

